import { Button } from 'design-system';
import { Mic, MicOff, PhoneCall, PhoneOff, Video, MonitorOff, Volume2, VolumeX } from 'lucide-react';
import { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import styles from './MessengerV2ActiveCallIndicator.module.css';
import { useMessengerV2ControllerContext } from './MessengerV2ControllerContext.jsx';
import { formatMessengerV2Message } from './messengerV2I18n.js';
import { buildMessengerV2CallPath } from './messengerV2Routes.js';
import useMessengerV2CallDuration from './useMessengerV2CallDuration.js';

const resolveCallStatus = (call, t, durationLabel = '') => {
  if (call?.status === 'outgoing') {
    return t('messenger_v2.call.calling', 'Calling...');
  }
  if (call?.status === 'reconnecting') {
    return t('messenger_v2.call.reconnecting', 'Reconnecting...');
  }
  if (call?.status === 'connecting') {
    return t('messenger_v2.call.connecting', 'Connecting...');
  }
  if (call?.status === 'ringing') {
    return t('messenger_v2.call.ringing', 'Ringing...');
  }
  if (durationLabel) return durationLabel;
  return call?.mode === 'video'
    ? t('messenger_v2.call.active_video', 'Video call')
    : t('messenger_v2.call.active', 'Voice call');
};

export const MessengerV2ActiveCallIndicator = () => {
  const controller = useMessengerV2ControllerContext();
  const intl = useIntl();
  const navigate = useNavigate();
  const call = controller?.voiceCall || {};
  const visible =
    call?.callId &&
    call?.status &&
    call.status !== 'idle' &&
    call.status !== 'incoming' &&
    call.presentation === 'background';
  const t = useCallback(
    (id, defaultMessage, values = undefined) =>
      formatMessengerV2Message(intl, id, defaultMessage, values),
    [intl],
  );
  const durationLabel = useMessengerV2CallDuration({
    enabled: visible && call.status === 'active',
    startedAt: call?.connectedAt || call?.startedAt || null,
  });

  if (!visible) return null;

  const status = resolveCallStatus(call, t, durationLabel);
  const name = String(call?.peerName || t('messenger_v2.common.messenger', 'Messenger'));
  const Icon = call?.mode === 'video' ? Video : PhoneCall;

  const handleOpenCall = () => {
    controller?.setVoiceCallPresentation?.({ presentation: 'foreground' });
    navigate(buildMessengerV2CallPath(call.callId, call.returnPath));
  };

  return (
    <div className={styles.bar} role="status">
      <div className={styles.controls} data-sharing={call.screenShareStatus === 'sharing' ? 'true' : 'false'}>
        <Button
          size="small"
          shape="circle"
          variant="text"
          className={styles.muteButton}
          aria-pressed={call.muted === true}
          aria-label={
            call.muted
              ? t('messenger_v2.call.unmute', 'Unmute')
              : t('messenger_v2.call.mute', 'Mute')
          }
          onClick={controller?.toggleVoiceCallMute}
        >
          {call.muted ? <MicOff size={18} aria-hidden /> : <Mic size={18} aria-hidden />}
        </Button>

        <Button
          variant="text"
          className={styles.trigger}
          classNames={{ content: styles.triggerContent }}
          onClick={handleOpenCall}
          aria-label={`${name}, ${status}`}
        >
          {/* <span className={styles.liveDot} aria-hidden /> */}
          <Icon size={16} aria-hidden />
          <span className={styles.name}>{name}</span>
          <span className={styles.status}>{status}</span>
        </Button>

        <Button size="small" shape="circle" variant="text" className={styles.soundButton}
          aria-pressed={call.soundMuted === true}
          aria-label={call.soundMuted ? t('messenger_v2.call.unmute_sound', 'Turn sound on') : t('messenger_v2.call.mute_sound', 'Turn sound off')}
          disabled={call.soundMutePending} onClick={controller?.toggleVoiceCallSound}>
          {call.soundMuted ? <VolumeX size={18} aria-hidden /> : <Volume2 size={18} aria-hidden />}
        </Button>
        {call.screenShareStatus === 'sharing' ? (
          <Button size="small" shape="circle" variant="text" className={styles.shareButton}
            aria-label={t('messenger_v2.call.stop_sharing', 'Stop sharing')}
            onClick={controller?.toggleVoiceCallScreenShare}>
            <MonitorOff size={18} aria-hidden />
          </Button>
        ) : null}
        <Button
          size="small"
          shape="circle"
          variant="text"
          className={styles.endButton}
          aria-label={t('messenger_v2.call.end', 'End call')}
          onClick={controller?.cancelVoiceCall}
        >
          <PhoneOff size={18} aria-hidden />
        </Button>
      </div>
    </div>
  );
};

export default MessengerV2ActiveCallIndicator;
